import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Der Pausen-Timer.
 *
 * Er rechnet gegen einen **Zeitpunkt**, nicht gegen einen heruntergezählten
 * Zustand: Auf dem iPhone schläft ein Hintergrund-Intervall ein, sobald der
 * Bildschirm ausgeht. Ein Zähler, der Sekunden addiert, stünde nach dem
 * Aufwachen falsch — der Vergleich mit der Uhr nicht.
 *
 * Kein Ton und keine Vibration: Beides ist in einer Web-App auf iOS ohne
 * Nutzergeste nicht verlässlich. Ein Timer, der manchmal piept, ist schlechter
 * als einer, der sichtbar läuft. In die Tasche reicht nur die Push-Mitteilung
 * vom Server (`restPush.ts`) — ob sie kommt, steht als `notice` darunter.
 *
 * Er ist die Kernzahl des Bildschirms, solange er läuft — zwischen zwei Sätzen
 * gibt es nichts, worauf man sonst schauen würde.
 */
export function RestTimer({
  endsAt,
  totalMs = null,
  notice = null,
  onDone,
  onCancel,
}: {
  endsAt: number;
  /** Länge der Pause, für den Balken. Ohne Angabe läuft nur die Zahl. */
  totalMs?: number | null;
  /** Ob das Telefon sich zum Ende meldet — nie verschweigen, wenn nicht. */
  notice?: string | null;
  onDone?: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(() => Math.max(0, endsAt - Date.now()));

  useEffect(() => {
    setRemaining(Math.max(0, endsAt - Date.now()));
    const handle = window.setInterval(() => {
      const left = Math.max(0, endsAt - Date.now());
      setRemaining(left);
      if (left === 0) {
        window.clearInterval(handle);
        onDone?.();
      }
    }, 250);
    return () => window.clearInterval(handle);
  }, [endsAt, onDone]);

  const seconds = Math.ceil(remaining / 1000);
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  const done = seconds === 0;

  // Der Balken leert sich, statt sich zu füllen: Was übrig ist, ist die Frage.
  const share = totalMs && totalMs > 0 ? Math.min(100, (remaining / totalMs) * 100) : null;

  return (
    <div className={`kw-hero ${done ? 'border-good' : ''}`}>
      <p className="kw-kicker">{t('training.rest.running')}</p>

      <p
        role="status"
        aria-live="polite"
        className={`kw-figure mt-2 text-5xl ${done ? 'text-good' : ''}`}
      >
        {done ? t('training.rest.done') : `${minutes}:${String(rest).padStart(2, '0')}`}
      </p>

      {share !== null && !done ? (
        <span className="kw-bar mt-3" aria-hidden="true">
          <i style={{ width: `${share}%` }} />
        </span>
      ) : null}

      {notice && !done ? <p className="mt-3 text-xs text-ink-muted">{notice}</p> : null}

      <button type="button" onClick={onCancel} className="kw-btn kw-btn-quiet mt-4 w-full">
        {done ? t('training.rest.dismiss') : t('training.rest.skip')}
      </button>
    </div>
  );
}
